import { useCallback, useEffect, useRef, useState } from 'react';
import ReferenceScreen from '../../components/reference/ReferenceScreen';
import RowStatus from '../../components/reference/RowStatus';
import FormAlert from '../../components/reference/FormAlert';
import { referenceApi } from '../../services/referenceApi';
import { apiErrorMessage, blankToNull, isNotFound } from '../../services/apiErrors';
import type { FuturesContract, Unit } from '../../services/referenceApi';
import { MONTH_LETTERS, monthNames } from '../../services/futuresMonths';

type Editing = { kind: 'new' } | { kind: 'edit'; row: FuturesContract } | null;

function byCode(a: FuturesContract, b: FuturesContract) {
  return a.code.localeCompare(b.code);
}

const EMPTY = { code: '', name: '', exchange: '', contractSize: '', unitId: '', poundsPerBushel: '', months: '' };

/** The futures contracts a buyer hedges on, each with the months it trades. */
export default function FuturesPage({ slug }: { slug: string }) {
  const [rows, setRows] = useState<FuturesContract[] | null>(null);
  const [units, setUnits] = useState<Unit[]>([]);
  // The next contract months of each contract, by contract id; worked out by the server.
  const [nextMonths, setNextMonths] = useState<Record<string, string>>({});
  const [loadError, setLoadError] = useState('');
  const [buyerMissing, setBuyerMissing] = useState(false);
  const [editing, setEditing] = useState<Editing>(null);
  const [form, setForm] = useState(EMPTY);
  const [saveError, setSaveError] = useState('');
  const [saving, setSaving] = useState(false);
  const [switching, setSwitching] = useState<string | null>(null);
  const [switchError, setSwitchError] = useState('');
  const firstFieldRef = useRef<HTMLInputElement>(null);
  const addButtonRef = useRef<HTMLButtonElement>(null);

  const loadNextMonths = useCallback(
    (contract: FuturesContract) => {
      referenceApi
        .listMonths(slug, contract.id)
        .then(({ data }) => setNextMonths((m) => ({ ...m, [contract.id]: data.map((d) => d.symbol).join(', ') })))
        // The list still works without them; the cell stays empty.
        .catch(() => undefined);
    },
    [slug]
  );

  useEffect(() => {
    let cancelled = false;
    setRows(null);
    setNextMonths({});
    setLoadError('');
    setBuyerMissing(false);
    setEditing(null);
    setSwitchError('');
    setSwitching(null);
    Promise.all([referenceApi.listContracts(slug), referenceApi.listUnits(slug)])
      .then(([contracts, unitList]) => {
        if (cancelled) return;
        setRows([...contracts.data].sort(byCode));
        setUnits(unitList.data);
        contracts.data.forEach(loadNextMonths);
      })
      .catch((err) => {
        if (cancelled) return;
        if (isNotFound(err)) setBuyerMissing(true);
        else setLoadError('We could not load the futures contracts just now. Check your connection and try again.');
      });
    return () => {
      cancelled = true;
    };
  }, [slug, loadNextMonths]);

  useEffect(() => {
    if (editing) firstFieldRef.current?.focus();
  }, [editing]);

  const unitById = (id: string) => units.find((u) => u.id === id);

  const sizeText = (row: FuturesContract) => {
    const size = Number(row.contract_size);
    const unit = unitById(row.unit_id)?.name.toLowerCase() ?? row.unit_code;
    return `${size.toLocaleString('en-US')} ${unit}${size === 1 ? '' : 's'}`;
  };

  const open = useCallback((next: Editing) => {
    setEditing(next);
    setSaveError('');
    setForm(
      next?.kind === 'edit'
        ? {
            code: next.row.code,
            name: next.row.name,
            exchange: next.row.exchange,
            contractSize: String(Number(next.row.contract_size)),
            unitId: next.row.unit_id,
            poundsPerBushel: next.row.pounds_per_bushel ? String(Number(next.row.pounds_per_bushel)) : '',
            months: next.row.listed_months,
          }
        : EMPTY
    );
  }, []);

  const set = (field: keyof typeof EMPTY) => (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) =>
    setForm((f) => ({ ...f, [field]: e.target.value }));

  const toggleMonth = (letter: string) =>
    setForm((f) => ({
      ...f,
      months: MONTH_LETTERS.map((m) => m.letter)
        .filter((l) => (l === letter ? !f.months.includes(l) : f.months.includes(l)))
        .join(''),
    }));

  const place = (saved: FuturesContract) => {
    setRows((current) => [...(current ?? []).filter((r) => r.id !== saved.id), saved].sort(byCode));
    loadNextMonths(saved);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!editing) return;
    setSaveError('');
    setSaving(true);
    const body = {
      name: form.name.trim(),
      exchange: form.exchange.trim(),
      contract_size: form.contractSize.trim(),
      unit_id: form.unitId,
      pounds_per_bushel: blankToNull(form.poundsPerBushel),
      listed_months: form.months,
    };
    try {
      const { data } =
        editing.kind === 'new'
          ? await referenceApi.addContract(slug, { ...body, code: form.code.trim().toUpperCase() })
          : await referenceApi.editContract(slug, editing.row.id, body);
      place(data);
      setEditing(null);
      addButtonRef.current?.focus();
    } catch (err) {
      setSaveError(apiErrorMessage(err, 'We could not save the contract just now. Check your connection and try again.'));
    } finally {
      setSaving(false);
    }
  };

  const toggle = async (row: FuturesContract) => {
    setSwitchError('');
    setSwitching(row.id);
    try {
      const { data } = await referenceApi.editContract(slug, row.id, { active: !row.active });
      place(data);
    } catch (err) {
      setSwitchError(apiErrorMessage(err, `We could not switch ${row.code} ${row.active ? 'off' : 'on'} just now.`));
    } finally {
      setSwitching(null);
    }
  };

  const unitOptions = units.filter((u) => u.active || u.id === form.unitId);
  const isBushel = unitById(form.unitId)?.code === 'bushel';
  const complete =
    (editing?.kind === 'edit' || form.code.trim()) &&
    form.name.trim() &&
    form.exchange.trim() &&
    form.contractSize.trim() &&
    form.unitId &&
    form.months;

  return (
    <ReferenceScreen
      slug={slug}
      title="Futures"
      lead={(buyer) =>
        `The futures contracts ${buyer} hedges on. The coming contract months are worked out from the months each one trades.`
      }
      addLabel="Add contract"
      onAdd={() => open({ kind: 'new' })}
      addButtonRef={addButtonRef}
      buyerMissing={buyerMissing}
    >
      {editing && (
        <form onSubmit={handleSubmit} className="mt-8 space-y-5 rounded-md border border-line bg-surface p-6" noValidate>
          <h2 className="text-heading text-ink">{editing.kind === 'new' ? 'New contract' : `Edit ${editing.row.code}`}</h2>
          <FormAlert lead="Contract not saved." message={saveError} />
          <div className="grid gap-5 sm:grid-cols-2">
            {editing.kind === 'new' && (
              <div>
                <label htmlFor="fc-code" className="label">
                  Code
                </label>
                <input
                  id="fc-code"
                  ref={firstFieldRef}
                  type="text"
                  required
                  maxLength={10}
                  aria-describedby="fc-code-hint"
                  value={form.code}
                  onChange={set('code')}
                  className="input uppercase tabular-nums"
                />
                <p id="fc-code-hint" className="mt-1 text-caption text-ink-muted">
                  The exchange&apos;s symbol, for example ZC for corn. It cannot change later.
                </p>
              </div>
            )}
            <div>
              <label htmlFor="fc-name" className="label">
                Name
              </label>
              <input
                id="fc-name"
                ref={editing.kind === 'edit' ? firstFieldRef : undefined}
                type="text"
                required
                maxLength={100}
                value={form.name}
                onChange={set('name')}
                className="input"
              />
            </div>
            <div>
              <label htmlFor="fc-exchange" className="label">
                Exchange
              </label>
              <input id="fc-exchange" type="text" required maxLength={20} value={form.exchange} onChange={set('exchange')} className="input" />
            </div>
            <div>
              <label htmlFor="fc-size" className="label">
                Contract size
              </label>
              <input
                id="fc-size"
                type="text"
                inputMode="decimal"
                required
                value={form.contractSize}
                onChange={set('contractSize')}
                className="input tabular-nums"
              />
            </div>
            <div>
              <label htmlFor="fc-unit" className="label">
                Unit
              </label>
              <select id="fc-unit" required value={form.unitId} onChange={set('unitId')} className="input">
                <option value="">Choose a unit</option>
                {unitOptions.map((u) => (
                  <option key={u.id} value={u.id}>
                    {u.name}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label htmlFor="fc-ppb" className="label">
                Pounds per bushel
              </label>
              <input
                id="fc-ppb"
                type="text"
                inputMode="decimal"
                aria-describedby="fc-ppb-hint"
                value={form.poundsPerBushel}
                onChange={set('poundsPerBushel')}
                className="input tabular-nums"
              />
              <p id="fc-ppb-hint" className="mt-1 text-caption text-ink-muted">
                {isBushel ? 'Needed for a contract sized in bushels, for example 56 for corn.' : 'Only for a contract sized in bushels.'}
              </p>
            </div>
          </div>
          <fieldset>
            <legend className="label">Months traded</legend>
            <div className="mt-2 grid grid-cols-3 gap-2 sm:grid-cols-6">
              {MONTH_LETTERS.map((m) => (
                <label key={m.letter} className="flex items-center gap-2 text-body text-ink">
                  <input type="checkbox" checked={form.months.includes(m.letter)} onChange={() => toggleMonth(m.letter)} />
                  {m.name} <span className="text-caption text-ink-muted">{m.letter}</span>
                </label>
              ))}
            </div>
          </fieldset>
          <div className="flex gap-3">
            <button type="submit" className="btn-primary" disabled={saving || !complete}>
              {saving ? 'Saving' : editing.kind === 'new' ? 'Save contract' : 'Save changes'}
            </button>
            <button type="button" className="btn-secondary" onClick={() => setEditing(null)} disabled={saving}>
              Cancel
            </button>
          </div>
        </form>
      )}

      <div className="mt-8">
        {switchError && <FormAlert lead="Not switched." message={switchError} />}
        {loadError && <FormAlert lead="Contracts not loaded." message={loadError} />}
        {rows === null && !loadError && (
          <p className="text-body text-ink-muted" role="status">
            Loading futures contracts
          </p>
        )}
        {rows !== null && rows.length === 0 && <p className="text-body text-ink-muted">No futures contracts yet.</p>}
        {rows !== null && rows.length > 0 && (
          // The table keeps its columns and scrolls inside its own box on a narrow screen. The box is
          // relative so the hidden Actions label stays inside it and cannot widen the page.
          <div className="relative overflow-x-auto">
            <table className="w-full border-collapse text-body">
              <caption className="sr-only">Futures contracts</caption>
              <thead>
                <tr className="border-b border-line-strong text-left text-caption font-semibold text-ink-muted">
                  <th scope="col" className="py-2 pr-4">
                    Code
                  </th>
                  <th scope="col" className="py-2 pr-4">
                    Name
                  </th>
                  <th scope="col" className="py-2 pr-4">
                    Exchange
                  </th>
                  <th scope="col" className="py-2 pr-4 text-right">
                    Contract size
                  </th>
                  <th scope="col" className="py-2 pr-4">
                    Months traded
                  </th>
                  <th scope="col" className="py-2 pr-4">
                    Next months
                  </th>
                  <th scope="col" className="py-2 pr-4">
                    Status
                  </th>
                  <th scope="col" className="py-2 text-right">
                    <span className="sr-only">Actions</span>
                  </th>
                </tr>
              </thead>
              <tbody>
                {rows.map((row) => (
                  <tr key={row.id} className={`border-b border-line ${row.active ? 'text-ink' : 'text-ink-muted'}`}>
                    <td className="py-3 pr-4 font-semibold tabular-nums">{row.code}</td>
                    <td className="py-3 pr-4">{row.name}</td>
                    <td className="py-3 pr-4">{row.exchange}</td>
                    <td className="py-3 pr-4 text-right tabular-nums">{sizeText(row)}</td>
                    <td className="py-3 pr-4">{monthNames(row.listed_months)}</td>
                    <td className="py-3 pr-4 tabular-nums">{nextMonths[row.id] ?? ''}</td>
                    <td className="py-3 pr-4">
                      <RowStatus active={row.active} />
                    </td>
                    <td className="py-3 text-right">
                      <div className="flex justify-end gap-3">
                        <button
                          type="button"
                          className="text-caption font-semibold text-navy underline-offset-4 hover:underline"
                          onClick={() => open({ kind: 'edit', row })}
                        >
                          Edit
                        </button>
                        <button
                          type="button"
                          className="text-caption font-semibold text-navy underline-offset-4 hover:underline disabled:opacity-60"
                          disabled={switching === row.id}
                          onClick={() => toggle(row)}
                        >
                          {row.active ? 'Switch off' : 'Switch on'}
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </ReferenceScreen>
  );
}
